import React, { useState, useEffect } from 'react';
import { useAuth, useDeviceLayout } from '../App';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Monitor, Smartphone, Save, Loader2, RotateCcw, Layout, Eye, PanelLeft, Search, Zap, Clock, BarChart3, Columns } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  desktop: {
    sidebar_visible: true, sidebar_collapsed: false, dashboard_columns: 4,
    card_style: 'default', show_header_search: true, show_quick_actions: true,
    show_recent_activity: true, show_stats_bar: true, content_max_width: '1400px'
  },
  mobile: {
    sidebar_visible: false, bottom_nav: true, dashboard_columns: 1,
    card_style: 'compact', show_header_search: false, show_quick_actions: true,
    show_recent_activity: false, show_stats_bar: true, content_max_width: '100%'
  }
};

const DevicePreviewTab = () => {
  const { token } = useAuth();
  const { refreshLayout } = useDeviceLayout();
  const [layout, setLayout] = useState({ desktop: { ...DEFAULTS.desktop }, mobile: { ...DEFAULTS.mobile } });
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchLayout = async () => {
      try {
        const res = await fetch(`${API_URL}/api/branding/device-layout`, { credentials: 'include', headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setLayout({
            desktop: { ...DEFAULTS.desktop, ...data.desktop },
            mobile: { ...DEFAULTS.mobile, ...data.mobile }
          });
        }
      } catch { /* use defaults */ }
      setLoading(false);
    };
    fetchLayout();
  }, [authHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/branding/device-layout`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(layout)
      });
      if (res.ok) { toast.success('Device layout saved'); refreshLayout(); }
      else toast.error('Failed to save');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleReset = () => {
    setLayout({ desktop: { ...DEFAULTS.desktop }, mobile: { ...DEFAULTS.mobile } });
    toast.success('Reset to defaults');
  };

  const updateConfig = (device, key, value) => {
    setLayout(prev => ({ ...prev, [device]: { ...prev[device], [key]: value } }));
  };

  const currentConfig = layout[previewDevice];

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="device-preview-tab">
      {/* Device toggle + actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-sm p-1">
          <button onClick={() => setPreviewDevice('desktop')}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${previewDevice === 'desktop' ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="preview-desktop-btn">
            <Monitor className="w-4 h-4" /> Desktop
          </button>
          <button onClick={() => setPreviewDevice('mobile')}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${previewDevice === 'mobile' ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
            data-testid="preview-mobile-btn">
            <Smartphone className="w-4 h-4" /> Mobile
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} className="border-white/10 text-white/60 hover:bg-white/5" data-testid="reset-layout-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="save-layout-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save Layout
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Controls */}
        <div className="w-full lg:w-80 flex-shrink-0 space-y-4">
          <h3 className="text-white/80 text-sm font-semibold flex items-center gap-2">
            <Layout className="w-4 h-4 text-primary" />
            {previewDevice === 'desktop' ? 'Desktop' : 'Mobile'} Layout Settings
          </h3>

          {/* Sidebar */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 space-y-3">
            <p className="text-white/60 text-xs font-medium flex items-center gap-1.5"><PanelLeft className="w-3.5 h-3.5" /> Navigation</p>
            <div className="flex items-center justify-between">
              <span className="text-white/70 text-xs">{previewDevice === 'desktop' ? 'Show Sidebar' : 'Show Bottom Nav'}</span>
              <Switch checked={previewDevice === 'desktop' ? currentConfig.sidebar_visible : currentConfig.bottom_nav}
                onCheckedChange={(v) => updateConfig(previewDevice, previewDevice === 'desktop' ? 'sidebar_visible' : 'bottom_nav', v)}
                data-testid="nav-toggle" />
            </div>
            {previewDevice === 'desktop' && currentConfig.sidebar_visible && (
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-xs">Collapsed (icons only)</span>
                <Switch checked={currentConfig.sidebar_collapsed}
                  onCheckedChange={(v) => updateConfig('desktop', 'sidebar_collapsed', v)}
                  data-testid="sidebar-collapsed-toggle" />
              </div>
            )}
          </div>

          {/* Dashboard */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 space-y-3">
            <p className="text-white/60 text-xs font-medium flex items-center gap-1.5"><Columns className="w-3.5 h-3.5" /> Dashboard Grid</p>
            <div className="flex items-center justify-between">
              <span className="text-white/70 text-xs">Columns</span>
              <Select value={String(currentConfig.dashboard_columns)} onValueChange={(v) => updateConfig(previewDevice, 'dashboard_columns', Number(v))}>
                <SelectTrigger className="w-20 h-7 bg-black/50 border-white/10 text-white text-xs" data-testid="columns-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  {[1, 2, 3, 4].map(n => <SelectItem key={n} value={String(n)} className="text-white text-xs">{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/70 text-xs">Card Style</span>
              <Select value={currentConfig.card_style} onValueChange={(v) => updateConfig(previewDevice, 'card_style', v)}>
                <SelectTrigger className="w-24 h-7 bg-black/50 border-white/10 text-white text-xs" data-testid="card-style-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="default" className="text-white text-xs">Default</SelectItem>
                  <SelectItem value="compact" className="text-white text-xs">Compact</SelectItem>
                  <SelectItem value="minimal" className="text-white text-xs">Minimal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-4 space-y-3">
            <p className="text-white/60 text-xs font-medium flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Visibility</p>
            {[
              { key: 'show_header_search', label: 'Header Search Bar', icon: Search },
              { key: 'show_quick_actions', label: 'Quick Actions', icon: Zap },
              { key: 'show_recent_activity', label: 'Recent Activity', icon: Clock },
              { key: 'show_stats_bar', label: 'Stats Bar', icon: BarChart3 },
            ].map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-white/70 text-xs flex items-center gap-1.5"><Icon className="w-3 h-3 text-white/40" />{label}</span>
                <Switch checked={!!currentConfig[key]}
                  onCheckedChange={(v) => updateConfig(previewDevice, key, v)}
                  data-testid={`toggle-${key}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Live Preview */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-primary" />
            <h3 className="text-white/80 text-sm font-semibold">Live Preview</h3>
            <span className="text-white/30 text-[10px] ml-auto">{previewDevice === 'desktop' ? '1440 x 900' : '375 x 812'}</span>
          </div>

          <div className={`mx-auto border-2 border-white/15 rounded-lg bg-[#080808] overflow-hidden ${previewDevice === 'desktop' ? 'max-w-[720px] aspect-[16/10]' : 'max-w-[240px] aspect-[9/19] rounded-3xl'}`}
            data-testid="device-preview-frame">
            {/* Title bar */}
            <div className={`bg-[#111] border-b border-white/10 flex items-center ${previewDevice === 'desktop' ? 'px-3 py-1.5' : 'px-2 py-1 justify-center'}`}>
              {previewDevice === 'desktop' ? (
                <>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-400/60" />
                    <div className="w-2 h-2 rounded-full bg-yellow-400/60" />
                    <div className="w-2 h-2 rounded-full bg-green-400/60" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="bg-white/5 rounded px-8 py-0.5 text-[7px] text-white/30 font-mono">securevault.app</div>
                  </div>
                </>
              ) : (
                <span className="text-[6px] text-white/30 font-mono">12:00</span>
              )}
            </div>

            {/* Content area */}
            <div className="flex h-[calc(100%-24px)]">
              {/* Sidebar preview (desktop) */}
              {previewDevice === 'desktop' && currentConfig.sidebar_visible && (
                <div className={`bg-[#0A0A0A] border-r border-white/10 flex-shrink-0 flex flex-col gap-1 py-2 ${currentConfig.sidebar_collapsed ? 'w-8 px-1' : 'w-24 px-2'}`}>
                  <div className={`rounded bg-primary/20 flex items-center gap-1 p-1 ${currentConfig.sidebar_collapsed ? 'justify-center' : ''}`}>
                    <div className="w-2 h-2 rounded bg-primary flex-shrink-0" />
                    {!currentConfig.sidebar_collapsed && <div className="h-1 bg-primary/40 rounded flex-1" />}
                  </div>
                  {['', '', '', '', ''].map((_, i) => (
                    <div key={i} className={`rounded flex items-center gap-1 p-1 ${currentConfig.sidebar_collapsed ? 'justify-center' : ''}`}>
                      <div className="w-2 h-2 rounded bg-white/10 flex-shrink-0" />
                      {!currentConfig.sidebar_collapsed && <div className={`h-1 bg-white/5 rounded`} style={{ width: `${40 + i * 8}%` }} />}
                    </div>
                  ))}
                </div>
              )}

              {/* Main content */}
              <div className="flex-1 overflow-hidden p-2">
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="h-2 bg-white/10 rounded w-16" />
                  {currentConfig.show_header_search && (
                    <div className="flex items-center gap-1 bg-white/5 rounded px-1.5 py-0.5">
                      <Search className="w-1.5 h-1.5 text-white/20" />
                      <div className="h-1 bg-white/5 rounded w-8" />
                    </div>
                  )}
                  <div className="w-3 h-3 rounded-full bg-primary/20" />
                </div>

                {/* Stats bar */}
                {currentConfig.show_stats_bar && (
                  <div className={`grid gap-1 mb-2`} style={{ gridTemplateColumns: `repeat(${Math.min(currentConfig.dashboard_columns, previewDevice === 'mobile' ? 2 : 4)}, 1fr)` }}>
                    {Array.from({ length: Math.min(4, currentConfig.dashboard_columns) }).map((_, i) => (
                      <div key={i} className={`bg-white/5 rounded border border-white/5 ${currentConfig.card_style === 'compact' ? 'p-1' : currentConfig.card_style === 'minimal' ? 'p-0.5' : 'p-1.5'}`}>
                        <div className="h-1 bg-white/10 rounded w-8 mb-1" />
                        <div className="h-2 bg-primary/20 rounded w-5" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick actions */}
                {currentConfig.show_quick_actions && (
                  <div className="flex gap-1 mb-2">
                    {['Upload', 'Share', 'Sign'].map(a => (
                      <div key={a} className="bg-primary/10 border border-primary/20 rounded px-1.5 py-0.5">
                        <span className="text-[5px] text-primary/60">{a}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Content grid */}
                <div className={`grid gap-1`} style={{ gridTemplateColumns: `repeat(${currentConfig.dashboard_columns}, 1fr)` }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className={`bg-white/[0.03] rounded border border-white/5 ${currentConfig.card_style === 'compact' ? 'p-1' : currentConfig.card_style === 'minimal' ? 'p-0.5 border-0' : 'p-1.5'}`}>
                      <div className="h-1 bg-white/8 rounded w-3/4 mb-1" />
                      <div className="h-1 bg-white/5 rounded w-1/2" />
                      {currentConfig.card_style === 'default' && <div className="h-1 bg-white/3 rounded w-2/3 mt-1" />}
                    </div>
                  ))}
                </div>

                {/* Recent activity */}
                {currentConfig.show_recent_activity && (
                  <div className="mt-2 space-y-0.5">
                    <div className="h-1 bg-white/8 rounded w-12 mb-1" />
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center gap-1 py-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
                        <div className="h-1 bg-white/5 rounded flex-1" />
                        <div className="h-1 bg-white/3 rounded w-6" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom nav (mobile) */}
            {previewDevice === 'mobile' && currentConfig.bottom_nav && (
              <div className="bg-[#0A0A0A] border-t border-white/10 flex items-center justify-around py-1">
                {['', '', '', ''].map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-0.5">
                    <div className={`w-2.5 h-2.5 rounded ${i === 0 ? 'bg-primary/40' : 'bg-white/10'}`} />
                    <div className="h-0.5 bg-white/5 rounded w-3" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevicePreviewTab;
